import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLocales, changeLanguage, getCurrentLanguage } from '../../i18n';
import './LanguageSelector.css';

const LanguageSelector = ({ className = '', size = 'md' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const dropdownRef = useRef(null);

  // 处理点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 处理语言变更
  const handleLanguageChange = async (langCode) => {
    try {
      await changeLanguage(langCode);
      setCurrentLang(langCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // 获取当前语言信息
  const getCurrentLangInfo = () => {
    return supportedLocales[currentLang] || supportedLocales['zh-CN'];
  };

  const currentLangInfo = getCurrentLangInfo();

  return (
    <div className={`language-selector ${className}`} ref={dropdownRef}>
      <button
        className={`language-selector__trigger language-selector__trigger--${size}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('language.change')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="language-selector__flag">
          {currentLangInfo.flag}
        </span>
        <span className="language-selector__name">
          {currentLangInfo.nativeName}
        </span>
        <svg 
          className={`language-selector__arrow ${isOpen ? 'language-selector__arrow--open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path 
            d="M3 4.5L6 7.5L9 4.5" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="language-selector__dropdown">
          <div className="language-selector__header">
            {t('language.select')}
          </div>
          <ul className="language-selector__list" role="listbox">
            {Object.entries(supportedLocales).map(([langCode, langInfo]) => (
              <li key={langCode} role="option" aria-selected={langCode === currentLang}>
                <button
                  className={`language-selector__option ${
                    langCode === currentLang ? 'language-selector__option--active' : ''
                  }`}
                  onClick={() => handleLanguageChange(langCode)}
                  disabled={langCode === currentLang}
                >
                  <span className="language-selector__option-flag">
                    {langInfo.flag}
                  </span>
                  <div className="language-selector__option-text">
                    <span className="language-selector__option-native">
                      {langInfo.nativeName}
                    </span>
                    <span className="language-selector__option-english">
                      {langInfo.name}
                    </span>
                  </div>
                  {langCode === currentLang && (
                    <svg 
                      className="language-selector__check" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16" 
                      fill="none"
                    >
                      <path 
                        d="M13.5 4.5L6 12L2.5 8.5" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
